// „Резервации по периоди" — един стълб на ред от таблицата.
//
// Същата графика като „Оборот по периоди" в „Продажби по периоди" (и на
// дашборда): числото над стълба, етикетът под него, пунктирана линия на
// средното. Разликата е само стойността — брой резервации, не евро. Много
// стълбове („във времето по часове") скролват в СВОЯ контейнер.

import { useTranslation } from "react-i18next";

import { formatNumber } from "../../../../components/dataTable";
import type { OccupancyPeriodRow } from "./occupancyMath";

const PLOT_HEIGHT = 132;
/** Над всеки стълб стои редът с числото — мащабът е върху остатъка. */
const BAR_SCALE_HEIGHT = PLOT_HEIGHT - 22;
const MIN_BAR_HEIGHT = 2;
/** Най-тясната колонка; под това етикетите се застъпват. */
const MIN_BAR_COLUMN_WIDTH = 44;

type Props = {
  rows: OccupancyPeriodRow[];
  total: number;
  isNarrow: boolean;
};

export const OccupancyChart = ({ rows, total, isNarrow }: Props) => {
  const { t } = useTranslation();

  const max = Math.max(1, ...rows.map((row) => row.reservations));
  const withReservations = rows.filter((row) => row.reservations > 0);
  const average = withReservations.length
    ? withReservations.reduce((sum, row) => sum + row.reservations, 0) /
      withReservations.length
    : 0;

  return (
    <section className="rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header
        className={`mb-3 flex gap-2.5 ${isNarrow ? "flex-col" : "items-baseline"}`}
      >
        <h2 className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.rvChartTitle")}
        </h2>
        <p className="text-[12px] font-semibold text-soft">
          {t("reportsPage.salesPeriodsChartHint")}
        </p>
        <span className="flex-1" />
        {average > 0 ? (
          <p className="font-pos-mono text-[12px] font-semibold tabular-nums text-muted">
            {t("reportsPage.rvChartAverage", { value: formatNumber(average, 1) })}
          </p>
        ) : null}
        <p className="font-pos-mono text-[12.5px] font-bold tabular-nums text-ink">
          {total}
        </p>
      </header>

      {withReservations.length ? (
        <div className="-mx-1 overflow-x-auto px-1">
          {/* Ширината идва от броя редове — стойност по време на изпълнение. */}
          <div style={{ minWidth: rows.length * MIN_BAR_COLUMN_WIDTH }}>
            <div className="relative flex h-[132px] items-end gap-1.5">
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 border-t border-dashed border-ghost"
                style={{ bottom: (average / max) * BAR_SCALE_HEIGHT }}
              />
              {rows.map((row) => (
                <div
                  key={row.key}
                  title={`${row.label}${row.sublabel ? ` · ${row.sublabel}` : ""} · ${row.reservations} · ${row.guests} ${t("reportsPage.rvKpiGuests").toLowerCase()}`}
                  className="flex h-full flex-1 flex-col items-center justify-end gap-1"
                >
                  <span className="font-pos-mono text-[10px] font-semibold tabular-nums text-brand-deep-ink">
                    {row.reservations || ""}
                  </span>
                  <span
                    className="w-full max-w-[44px] rounded-t-[4px] bg-brand"
                    style={{
                      height: Math.max(
                        MIN_BAR_HEIGHT,
                        (row.reservations / max) * BAR_SCALE_HEIGHT,
                      ),
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="mt-1.5 flex gap-1.5">
              {rows.map((row) => (
                <span
                  key={row.key}
                  className="flex min-w-0 flex-1 flex-col items-center gap-0.5 text-center font-pos-mono text-[10px] leading-none"
                  title={row.sublabel ? `${row.label} · ${row.sublabel}` : row.label}
                >
                  <span className="max-w-full truncate font-bold text-ink">
                    {row.chartLabel}
                  </span>
                  {row.chartSublabel ? (
                    <span className="max-w-full truncate font-semibold text-muted">
                      {row.chartSublabel}
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="py-8 text-center text-[12.5px] font-medium text-muted">
          {t("reportsPage.rvChartEmpty")}
        </p>
      )}
    </section>
  );
};
